// Hours.js
import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./Hours.css";

// --- YouTube Player API Loader ---
const loadYouTubeAPI = () => {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
    document.body.appendChild(tag);
  });
};

// --- Tab focus detection hook ---

const useTabFocusGuard = (onViolation) => {
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) onViolation();
    };
    window.addEventListener("blur", onViolation); // also catches minimize
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("blur", onViolation);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [onViolation]);
};

const Hours = () => {
  const [activeTab, setActiveTab] = useState("unit1");
  const [volunteerHours, setVolunteerHours] = useState(0);
  const [badge, setBadge] = useState("none");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [lastWatched, setLastWatched] = useState({ unit1: 0, unit2: 0, unit3: 0 });
  const [completedUnits, setCompletedUnits] = useState({ unit1: false, unit2: false, unit3: false });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  // Quiz modal / state (unit tests)
  const [showTestModal, setShowTestModal] = useState(false);
  const [testUnitKey, setTestUnitKey] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]); // store index or null
  const [testScore, setTestScore] = useState(null);
  const [testPassed, setTestPassed] = useState(null);

  // Final exam (big quiz) state
  const [showFinalModal, setShowFinalModal] = useState(false);
  const [finalQuestions, setFinalQuestions] = useState([]); // will hold shuffled big quiz questions
  const [finalAnswers, setFinalAnswers] = useState([]); // chosen indices for final quiz
  const [finalIndex, setFinalIndex] = useState(0);
  const [finalScore, setFinalScore] = useState(null);
  const [finalPassed, setFinalPassed] = useState(null);
  const [finalAttempted, setFinalAttempted] = useState(false);

  useTabFocusGuard(() => {
  if (showTestModal) {
    alert("❌ Quiz attempt cancelled — you switched tabs or minimized!");
    closeTestModal();
  }
  if (showFinalModal) {
    alert("❌ Final exam attempt cancelled — you switched tabs or minimized!");
    closeFinalModal();
  }
});

  // Questions: Unit1 uses your provided exact questions & answers (0-based correct index)
  const unitQuestions = {
    unit1: [
      {
        q: "In the script, cyber security is compared to locking your doors at home. What is the deeper implication of this analogy?",
        options: [
          "Protection requires both tools and proactive habits",
          "Cyber threats only occur at night",
          "Devices must be physically secured",
          "Only businesses need strict security"
        ],
        correct: 0
      },
      {
        q: "Why is everyone vulnerable to cyber threats and not just businesses?",
        options: [
          "Because only social media users are targeted",
          "Because personal data online can be exploited by criminals",
          "Because individuals store more data than companies",
          "Because individuals rarely update their devices"
        ],
        correct: 1
      },
      {
        q: "A person receives a link that leads to a realistic-looking login page designed to steal credentials. This attack best aligns with:",
        options: [
          "Malware",
          "Ransomware",
          "Unauthorized hacking",
          "Phishing",
        ],
        correct: 3
      },
      {
        q: "Social media is a hotspot for cyber criminals. What is the primary danger that we explained?",
        options: [
          "Social media accounts use weak encryption",
          "Criminals use social media to gather personal info and impersonate users",
          "Social media automatically exposes passwords",
          "Social media sites do not support two-factor authentication"
        ],
        correct: 1
      },
      {
        q: "Enabling two-factor authentication reduces risk by:",
        options: [
          "Encrypting all user data end-to-end",
          "Requiring a one-time code even if the password is stolen",
          "Blocking all unfamiliar IP addresses",
          "Making passwords unnecessary"
        ],
        correct: 1
      },
      {
        q: "Why is it strongly discouraged to use passwords like “123456” or “ABCD”?",
        options: [
          "Hackers can guess simple patterns extremely easily",
          "These passwords cannot be saved in password managers",
          "They interfere with two-factor authentication",
          "They cause software update errors"
        ],
        correct: 0
      },
      {
        q: "Ransomware locks you out of your files until payment is made. What underlying vulnerability makes this possible?",
        options: [
          "The victim clicking on a phishing link",
          "The operating system failing to boot",
          "Malware gaining privileged access to the system",
          "Social media accounts being set to public"
        ],
        correct: 2
      },
      {
        q: "Which behavior is MOST strongly linked to preventing phishing attacks?",
        options: [
          "Updating software regularly",
          "Using a unique password for each account",
          "Setting social media profiles to private",
          "Being cautious about unfamiliar links or emails",
        ],
        correct: 3
      },
      {
        q: "What inference was made about software updates and cyber threats?",
        options: [
          "Updates make devices more vulnerable",
          "Updates only improve performance, not security",
          "Updates fix security weaknesses that hackers could exploit",
          "Updates prevent phishing attacks entirely"
        ],
        correct: 2
      },
      {
        q: "Why is making social media accounts private considered a cyber security method rather than just a privacy choice?",
        options: [
          "It stops malware from spreading through social networks",
          "It prevents unknown users from gathering exploitable personal data",
          "It disables the ability to click on outside links",
          "It automatically encrypts all personal posts"
        ],
        correct: 1
      }
    ],
    // placeholders for unit2 and unit3 - replace these later when you provide questions
    unit2: [
  {
    q: "A cousin receives a message asking for their password from someone claiming to be a friend. What is the main cyber security risk here?",
    options: [
      "Malware installation",
      "Social engineering",
      "Denial-of-service attack",
      "Ransomware"
    ],
    correct: 1
  },
  {
    q: "Which of the following is an example of phishing?",
    options: [
      "Spamming random ads online",
      "Sending a virus in a USB stick",
      "Pretending to be a friend to ask for login info via email",
      "Locking someone’s files and demanding money"
    ],
    correct: 2
  },
  {
    q: "Pre-texting differs from phishing because:",
    options: [
      "It encrypts the victim’s files",
      "It only targets social media accounts",
      "It uses malware to steal data",
      "It pretends to need personal information for a legitimate reason"
    ],
    correct: 3
  },
  {
    q: "Baiting relies on:",
    options: [
      "Offering something free in exchange for login credentials or access",
      "Using weak passwords",
      "Hacking into secure networks",
      "Physically stealing devices"
    ],
    correct: 0
  },
  {
    q: "Why is critical thinking important in preventing social engineering attacks?",
    options: [
      "It disables phishing links automatically",
      "It improves software update speed",
      "It allows users to hack back safely",
      "It helps users identify and question suspicious requests"
    ],
    correct: 3
  },
  {
    q: "Cyber bullying can have long-term consequences because:",
    options: [
      "Messages are always encrypted",
      "Online posts affect digital footprint, reputation, and mental health",
      "Bullies can hack your devices",
      "It cannot be reported once posted"
    ],
    correct: 1
  },
  {
    q: "If someone witnesses cyber bullying, what is the recommended first step?",
    options: [
      "Take screenshots as evidence",
      "Delete your own social media account",
      "Respond to the bully aggressively",
      "Contact the police immediately"
    ],
    correct: 0
  },
  {
    q: "Why is it important to report cyber bullying to the platform and an adult?",
    options: [
      "To change your digital footprint",
      "To prevent account suspension",
      "To automatically block all users",
      "To ensure proper action and protect the victim"
    ],
    correct: 2
  },
  {
    q: "What does the term “digital footprint” mean?",
    options: [
      "The permanent record of everything you post online",
      "Your IP address history",
      "Your antivirus scan results",
      "The software installed on your device"
    ],
    correct: 0
  },
  {
    q: "How can online behavior impact real-life opportunities?",
    options: [
      "It can prevent your device from being hacked",
      "It automatically deletes all posts after 30 days",
      "Social media monitors your grades directly",
      "Colleges, employers, and others may see your posts and judge you based on them"
    ],
    correct: 3
  }
],
    unit3: [
  {
    q: "What is the main role of an ethical hacker?",
    options: [
      "Simulating attacks to find vulnerabilities before criminals do",
      "Stealing confidential company data",
      "Monitoring employee devices for personal use",
      "Creating harmful viruses to test defenses"
    ],
    correct: 0
  },
  {
    q: "Ethical hackers are also known as:",
    options: [
      "Malware engineers",
      "Grey hat hackers",
      "White hat hackers",
      "Black hat hackers"
    ],
    correct: 2
  },
  {
    q: "What is penetration testing?",
    options: [
      "Installing antivirus programs on devices",
      "Encrypting sensitive information for storage",
      "A simulated attack to uncover system weaknesses",
      "Breaking into competitor networks for intel"
    ],
    correct: 2
  },
  {
    q: "Why is ethical hacking considered legal, unlike regular hacking?",
    options: [
      "It uses only government-approved tools",
      "It never targets sensitive data",
      "It does not involve computers",
      "Ethical hackers have permission and work to protect systems"
    ],
    correct: 3
  },
  {
    q: "Cyber security policies are best described as:",
    options: [
      "Rules and guidelines an organisation sets to protect systems and data",
      "A method for legally hacking other companies",
      "A government-controlled network monitor",
      "A type of penetration test"
    ],
    correct: 0
  },
  {
    q: "Compliance in cyber security refers to:",
    options: [
      "Monitoring social media accounts",
      "Hiring ethical hackers regularly",
      "Encrypting all company files",
      "Following laws and regulations like GDPR or COPPA"
    ],
    correct: 3
  },
  {
    q: "Which of the following is an example of a compliance law?",
    options: [
      "General Data Protection Regulation (GDPR)",
      "Online Social Media Guidelines",
      "Digital Safety Hackers Act",
      "Phishing Prevention Regulation"
    ],
    correct: 0
  },
  {
    q: "How do policies and compliance work together in an organization?",
    options: [
      "They only apply during cyber attacks",
      "Both are optional recommendations",
      "Policies guide employee behaviour; compliance ensures legal requirements are met",
      "Policies stop malware; compliance removes viruses"
    ],
    correct: 2
  },
  {
    q: "What is the main goal of companies following cyber security policies and compliance regulations?",
    options: [
      "Boosting social media engagement",
      "Avoiding software updates",
      "Eliminating IT departments",
      "Increasing user trust and protecting sensitive data"
    ],
    correct: 3
  },
  {
    q: "Which of the following best summarizes ethical hacking?",
    options: [
      "Encrypting all company data without notice",
      "Helping organisations by testing their systems with permission",
      "Monitoring employee personal messages",
      "Gaining illegal access to systems"
    ],
    correct: 1
  }
]

  };
  const bigQuizBank = [
  // merged questions (shuffled manually and varied)
  {
    q: "What best defines the role of cybersecurity?",
    options: [
      "Regulating internet use for government purposes",
      "Safeguarding digital systems and data from unauthorized access and threats",
      "Designing applications to improve social media interaction",
      "Creating faster software for online communication"
    ],
    correct: 1
  },
  {
    q: "Which of the following is considered a deliberate cyber threat?",
    options: [
      "Outdated software slowing down a device",
      "Phishing emails mimicking trusted sources",
      "Poor Wi-Fi connection",
      "Forgetting your social media password"
    ],
    correct: 1
  },
  {
    q: "Why has cybersecurity become increasingly important in recent years?",
    options: [
      "Because all devices come with built-in antivirus software",
      "Because it's now required in school curriculums",
      "Because cybercriminals can now control the entire internet",
      "Because digital activities like banking, shopping, and working are more common"
    ],
    correct: 3
  },
  {
    q: "In the script, what metaphor is used to describe cybersecurity?",
    options: [
      "Hiding your wallet in your sock",
      "Turning off the lights when you leave a room",
      "Locking doors to protect against burglars",
      "Wearing gloves during flu season"
    ],
    correct: 2
  },
  {
    q: "Which combination below includes actual cyber threats?",
    options: [
      "Phishing, ransomware, data breaches",
      "Ransomware, two-factor authentication, phishing",
      "Malware, spam folders, multi-layered encryption",
      "Viruses, screen brightness, weak Wi-Fi"
    ],
    correct: 0
  },
  {
    q: "How do cybercriminals commonly exploit social media?",
    options: [
      "By creating fake profiles or collecting personal data to trick users",
      "By deleting inactive accounts",
      "By analyzing ad algorithms",
      "By posting viral content to gain followers"
    ],
    correct: 0
  },
  {
    q: "Which of the following sets contains information that could be used for identity theft?",
    options: [
      "Screen brightness, sleep schedule, battery percentage",
      "Number of followers, emojis, hashtags",
      "Username, favorite movie, city skyline photo",
      "Birthday, address, job title"
    ],
    correct: 3
  },
  {
    q: "Who is at risk of being a victim of a cyber attack?",
    options: [
      "Only people who use public Wi-Fi",
      "Only tech companies with large customer databases",
      "Every individual or organization connected to the internet",
      "Only people who download illegal content"
    ],
    correct: 2
  },

  // Quiz 2 style questions (malware / phishing / 2FA / updates)
  {
    q: "What is malware?",
    options: [
      "A browser feature that manages internet speed",
      "An email attachment that improves device performance",
      "A type of software that protects against viruses",
      "Malicious software that can damage or steal data"
    ],
    correct: 3
  },
  {
    q: "Which of the following is an example of malware?",
    options: [
      "Firewall",
      "Trojan",
      "CAPTCHA",
      "Encryption"
    ],
    correct: 1
  },
  {
    q: "What is phishing?",
    options: [
      "Fake emails or websites designed to collect personal information",
      "Malicious code that disables your system",
      "Secure messaging used for online transactions",
      "A tool for password recovery"
    ],
    correct: 0
  },
  {
    q: "An email that contains a suspicious link asking for login credentials is most likely:",
    options: [
      "A security update",
      "A system error notification",
      "A ransomware warning",
      "A phishing attempt"
    ],
    correct: 3
  },
  {
    q: "What does ransomware do?",
    options: [
      "Encrypts personal data and shares it publicly",
      "Locks access to files and demands payment for restoration",
      "Destroys hardware remotely",
      "Deletes all passwords from saved accounts"
    ],
    correct: 1
  },
  {
    q: "What is hacking?",
    options: [
      "Creating secure logins for websites",
      "Gaining unauthorized access to devices or networks",
      "Repairing corrupted files",
      "Blocking internet connections for protection"
    ],
    correct: 1
  },
  {
    q: "What is a strong password practice?",
    options: [
      "Trying different combinations using tools like Password Monster",
      "Saving passwords in a text file on your desktop",
      "Using short words and common numbers",
      "Reusing the same password across all platforms"
    ],
    correct: 0
  },
  {
    q: "Why should simple passwords like “123456” be avoided?",
    options: [
      "They take up too much memory",
      "They expire quickly",
      "They are predictable and easily guessed",
      "They cause system errors during logins"
    ],
    correct: 2
  },
  {
    q: "What does Two-Factor Authentication (2FA) add to an account?",
    options: [
      "Automatic password resets",
      "An extra verification step for added security",
      "Faster login access",
      "Unlimited data backups"
    ],
    correct: 1
  },
  {
    q: "Why are software updates important?",
    options: [
      "They delete unused apps",
      "They reduce battery usage",
      "They improve display quality",
      "They fix security flaws that attackers could exploit"
    ],
    correct: 3
  },
  {
    q: "What should you do with an unknown email containing a link or attachment?",
    options: [
      "Open it on your phone instead",
      "Forward it to your contact list",
      "Avoid clicking or downloading anything unfamiliar",
      "Respond to ask if it’s legitimate"
    ],
    correct: 2
  },
  {
    q: "What is a safe way to manage your social media privacy?",
    options: [
      "Post your address to confirm identity",
      "Accept requests from unknown accounts",
      "Set profiles to private and limit what you share",
      "Keep profiles public for transparency"
    ],
    correct: 2
  },

  // Unit 1 15-question pool (selected/jumbled)
  {
    q: "What is cybersecurity?",
    options: [
      "Protecting systems, networks, and data from cyber threats",
      "A way to design video games",
      "Creating social media accounts",
      "The study of computers"
    ],
    correct: 0
  },
  {
    q: "Which of these is considered a cyber threat?",
    options: [
      "Firewall",
      "Malware",
      "Two-factor authentication",
      "Software update"
    ],
    correct: 1
  },
  {
    q: "What is the purpose of two-factor authentication (2FA)?",
    options: [
      "To add an extra layer of security to your account",
      "To allow password sharing",
      "To back up your data",
      "To delete your browsing history"
    ],
    correct: 0
  },
  {
    q: "What kind of attack involves tricking users into giving up personal information through fake emails?",
    options: [
      "Phishing",
      "Malware",
      "Hacking",
      "Coding"
    ],
    correct: 0
  },
  {
    q: "Why is using the same password on every site dangerous?",
    options: [
      "It blocks updates",
      "It makes websites load slower",
      "It can make it easier for hackers to access all your accounts",
      "It improves your security"
    ],
    correct: 2
  },
  {
    q: "What is an example of strong password hygiene?",
    options: [
      "Using unique passwords for every account",
      "Using “123456”",
      "Writing your passwords on sticky notes",
      "Reusing your birthday on every site"
    ],
    correct: 0
  },
  {
    q: "Which of these is NOT a cyber threat?",
    options: [
      "Ransomware",
      "Hacking",
      "Social engineering",
      "Two-factor authentication"
    ],
    correct: 3
  },
  {
    q: "Why is it risky to post personal information on social media?",
    options: [
      "It can be used by cybercriminals for scams or identity theft",
      "It lowers your follower count",
      "It improves your password strength",
      "It speeds up your apps"
    ],
    correct: 0
  },
  {
    q: "What should you do before accepting a friend request online?",
    options: [
      "Share your password first",
      "Accept it quickly to grow your follower count",
      "Check if you know the person",
      "Ignore it every time"
    ],
    correct: 2
  },
  {
    q: "What is hacking?",
    options: [
      "Gaining unauthorized access to data or systems",
      "Posting online",
      "Changing your profile picture",
      "Logging into your account"
    ],
    correct: 0
  },

  // True/False style adapted to multiple choice (last 5)
  {
    q: "Phishing emails are always easy to spot because they contain spelling errors.",
    options: [
      "True",
      "False"
    ],
    correct: 1
  },
  {
    q: "Updating your software regularly helps protect your device from cyber threats.",
    options: [
      "True",
      "False"
    ],
    correct: 0
  },
  {
    q: "Cybersecurity only matters for adults who work in tech.",
    options: [
      "True",
      "False"
    ],
    correct: 1
  },
  {
    q: "Hackers can use fake social media accounts to trick people.",
    options: [
      "True",
      "False"
    ],
    correct: 0
  },
  {
    q: "Using “password123” is a strong and secure password.",
    options: [
      "True",
      "False"
    ],
    correct: 1
  }
];

  const units = {
    unit1: {
      title: "Unit 1 — Cyber Basics",
      count: 11,
      videos: [
        { id: 1, title: "Cyber Basics — Part 1", url: "https://www.youtube.com/embed/U-wpswQHtKI?si=hrsf9jNmu4Or7hOG" },
        { id: 2, title: "Cyber Basics — Part 2", url: "https://www.youtube.com/embed/HaaH67wgcm8?si=zuJUFkKx5z9T9wpx" },
        { id: 3, title: "Cyber Basics — Part 3", url: "https://www.youtube.com/embed/Zk-OiGboG6g?si=v480M25UJUiY8FL9" },
        { id: 4, title: "Cyber Basics — Part 4", url: "https://www.youtube.com/embed/rJbwdc8cvS0?si=bMEKVkaj4E0n8rLM" },
        { id: 5, title: "Cyber Basics — Part 5", url: "https://www.youtube.com/embed/I5JYKs1ycFQ?si=1DOmbjS6hUB8uASi" },
        { id: 6, title: "Cyber Basics — Part 6", url: "https://www.youtube.com/embed/sTh63FYrEGM?si=dNZztqQK0dYZrjCD" },
        { id: 7, title: "Cyber Basics — Part 7", url: "https://www.youtube.com/embed/xAFtNrm1IZw?si=gndrwZRJLCgY8j6W" },
        { id: 8, title: "Cyber Basics — Part 8", url: "https://www.youtube.com/embed/PESJJY8MHi8?si=FTAmC87Dz6LXL-BC" },
        { id: 9, title: "Cyber Basics — Part 9", url: "https://www.youtube.com/embed/NeT22gnCLtY?si=1WG35XdJ61vqdg0y" },
        { id: 10, title: "Cyber Basics — Part 10", url: "https://www.youtube.com/embed/ohRI4TH4PaU?si=c6-UOA8UvvntCWtD" },
        { id: 11, title: "Cyber Basics — Part 11", url: "https://www.youtube.com/embed/nXoaENm2fd4?si=cVMX06fqUPhVBIqv" },
      ],
    },
    unit2: {
      title: "Unit 2 — Network Security",
      count: 2,
      videos: [
        { id: 1, title: "Network Security — Part 1", url: "https://www.youtube.com/embed/C7YVTwdQL80?si=v5urHp0H--ozH2GU" },
        { id: 2, title: "Network Security — Part 2", url: "https://www.youtube.com/embed/tVi8UW4297s?si=L75TMyx9XgtLpLYy" },
      ],
    },
    unit3: {
      title: "Unit 3 — Ethical Hacking",
      count: 5,
      videos: [
        { id: 1, title: "Ethical Hacking — Part 1", url: "https://www.youtube.com/embed/xJmn6CShKk8?si=FrQxxA6mkXXuw034" },
        { id: 2, title: "Ethical Hacking — Part 2", url: "https://www.youtube.com/embed/ZzsJ0_ycheU?si=8vwKcoL8PdOaOg_v" },
        { id: 3, title: "Ethical Hacking — Part 3", url: "https://www.youtube.com/embed/OpHewZAC8Nk?si=LpzbbUmZdnqeHbBw" },
        { id: 4, title: "Ethical Hacking — Part 4", url: "https://www.youtube.com/embed/_kXAh_BmOWc?si=zYjzWJ5b1C4_cuNn" },
        { id: 5, title: "Ethical Hacking — Part 5", url: "https://www.youtube.com/embed/fFTomY5I27s?si=QCeN6kPPdFzexXTo" },
      ],
    },
  };
  // Load user and progress from Firestore (with proper realtime listener + cleanup)
  useEffect(() => {
    let unsubscribeSnapshot = null;
    const unsubscribeAuth = auth.onAuthStateChanged(async (u) => {
      try {
        // No user or not verified => show login popup
    if (!u) {
  if (unsubscribeSnapshot) unsubscribeSnapshot();
  setShowLoginPopup(true);
  setLoading(false);
  setUser(null);
  return;
}

    // IMPORTANT: do NOT return here
setUser(u);
setShowLoginPopup(!u.emailVerified);


        // Root user document (profile is a map field inside this document)
        const userRef = doc(db, "users", u.uid);
        if (unsubscribeSnapshot) unsubscribeSnapshot();

        // Ensure document exists with profile map (initialization)
        // Ensure document exists with profile map (initialization)
const initialSnap = await getDoc(userRef);
if (!initialSnap.exists()) {
  await setDoc(
    userRef,
    {
      profile: {
        progress: {
          unit1: { lastWatched: 0, completed: false },
          unit2: { lastWatched: 0, completed: false },
          unit3: { lastWatched: 0, completed: false },
        },
      },
    },
    { merge: true }
  );
}


        // Attach realtime listener (onSnapshot)
        unsubscribeSnapshot = onSnapshot(
          userRef,
          (snap) => {
            if (!snap.exists()) return;
            const data = snap.data() || {};
            const profile = data.profile || {};
            const progress = profile.progress || {};

            // Update lastWatched and completedUnits
            // Update lastWatched and completedUnits
          setLastWatched({
            unit1: progress.unit1?.lastWatched || 0,
            unit2: progress.unit2?.lastWatched || 0,
            unit3: progress.unit3?.lastWatched || 0,
          });
          setCompletedUnits({
            unit1: !!progress.unit1?.completed,
            unit2: !!progress.unit2?.completed,
            unit3: !!progress.unit3?.completed,
          });

          // Update awarded units tracking

          // Update volunteer hours & badge from profile map - use the exact value from Firestore
          const hours = Number(profile.volunteerHours || 0);
          setVolunteerHours(hours);

            if (hours >= 100) setBadge("platinum");
            else if (hours >= 50) setBadge("gold");
            else if (hours >= 10) setBadge("silver");
            else if (hours >= 5) setBadge("bronze");
            else setBadge("none");
          },
          (err) => {
            console.error("onSnapshot error:", err);
          }
        );

        setLoading(false);
      } catch (err) {
        console.error("Error loading user data:", err);
        setLoading(false);
      }
    });

    // cleanup both auth and snapshot listeners on unmount
    return () => {
      if (typeof unsubscribeAuth === "function") unsubscribeAuth();
      if (typeof unsubscribeSnapshot === "function") unsubscribeSnapshot();
    };
  }, []);

  useEffect(() => {
  let ytPlayer = null;

  const setupYT = async () => {
    if (!selectedVideo || !user) return;
    const YT = await loadYouTubeAPI();

    // Clear old player container
    const container = document.getElementById("yt-player-container");
    if (container) container.innerHTML = "";

    const videoId = selectedVideo.url.split("embed/")[1]?.split("?")[0];
    if (!videoId) return;

    // Create YouTube player
    ytPlayer = new YT.Player("yt-player-container", {
      videoId,
      events: {
        onStateChange: async (event) => {
          if (event.data === YT.PlayerState.ENDED) {
            console.log("✅ Video completed:", selectedVideo.title);

            try {
              const userRef = doc(db, "users", user.uid);

await updateDoc(userRef, {
  [`profile.progress.${selectedVideo.unit}.lastWatched`]: selectedVideo.id,
});

setLastWatched((prev) => ({
  ...prev,
  [selectedVideo.unit]: selectedVideo.id,
}));
            } catch (err) {
              console.error("Error marking video watched:", err);
            }
          }
        },
      },
    });
  };

  setupYT();

  return () => {
    if (ytPlayer && ytPlayer.destroy) ytPlayer.destroy();
  };
}, [selectedVideo, user]);
  // loading state
  if (loading) {
    return (
      <div className="loading-screen">
        <h2>Loading your dashboard...</h2>
      </div>
    );
  }

  // popup for non-logged-in or unverified users
  if (showLoginPopup) {
    return (
      <div className="login-popup-overlay">
        <div className="login-popup">
          <h2>Please Log In / Verify Email</h2>
          <p>You must verify your email and sign in to access the dashboard.</p>
          <button className="login-redirect-btn" onClick={() => (window.location.href = "/login")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // helper: whether a unit is unlocked (previous unit's test must be passed or it's unit1)
  const unitUnlocked = (unitKey) => {
    if (unitKey === "unit1") return true;
    if (unitKey === "unit2") return completedUnits.unit1;
    if (unitKey === "unit3") return completedUnits.unit2;
    return false;
  };

  // node status: watched | available | locked
  const nodeStatus = (unitKey, id) => {
    const lw = lastWatched[unitKey] || 0;
    if (id <= lw) return "watched";
    if (id === lw + 1) return "available";
    return "locked";
  };

  // when user clicks a tab (unit)
  const handleTabClick = (unitKey) => {
    if (!unitUnlocked(unitKey)) {
      alert("This unit is locked. Complete the previous unit test first.");
      return;
    }
    setActiveTab(unitKey);
  };

  // when opening a video: enforce sequential rule and update Firestore progress
  const tryOpenVideo = async (video) => {
    if (!unitUnlocked(activeTab)) {
      alert("This unit is locked.");
      return;
    }

    const lw = lastWatched[activeTab] || 0;
    if (video.id > lw + 1) {
      alert(`Please watch Video ${lw + 1} first to proceed in order.`);
      return;
    }

    // open modal with autoplay param
    const autoplayUrl = video.url.includes("?") ? `${video.url}&autoplay=1` : `${video.url}?autoplay=1`;
    setSelectedVideo({ ...video, url: autoplayUrl, unit: activeTab });

    // update progress: mark this video as watched (lastWatched)
    
  };

  // handle closing video modal
  const closeModal = () => {
    setSelectedVideo(null);
  };

  // Start the unit test (opens modal) — only if all videos in unit are watched (renderUnitNodes also enforces)
  const startUnitTest = (unitKey) => {
    const allWatched = (lastWatched[unitKey] || 0) >= units[unitKey].count;
    if (!allWatched) {
      alert("You must watch all videos in this unit before taking the unit test.");
      return;
    }

    const questions = unitQuestions[unitKey] || unitQuestions.unit1;
    setTestUnitKey(unitKey);
    setSelectedAnswers(new Array(questions.length).fill(null));
    setCurrentQuestionIndex(0);
    setTestScore(null);
    setTestPassed(null);
    setShowTestModal(true);
  };

  // handle selecting an answer
  const chooseAnswer = (qIndex, optIndex) => {
    setSelectedAnswers((prev) => {
      const copy = [...prev];
      copy[qIndex] = optIndex;
      return copy;
    });
  };

  // submit test and evaluate score
  const submitTest = async () => {
    const questions = unitQuestions[testUnitKey] || unitQuestions.unit1;
    // score calculation (treat unanswered as wrong)
    let score = 0;
    for (let i = 0; i < questions.length; i++) {
      if (selectedAnswers[i] === questions[i].correct) score++;
    }
    setTestScore(score);
    const passed = score >= 8;
    setTestPassed(passed);

    // if passed => update firestore: mark unit complete and add 1 volunteer hour
    // if passed => update firestore: mark unit complete and add 1 volunteer hour ONLY if not already awarded
if (passed) {
  if (!user) {
    alert("You must be logged in for your completion to be recorded.");
    return;
  }

  try {
    const functions = getFunctions(undefined, "us-central1");
    const awardUnitCompletion = httpsCallable(functions, "awardUnitCompletion");

    await awardUnitCompletion({ unit: testUnitKey });

    alert(`Congratulations — you passed ${units[testUnitKey].title}!`);
  } catch (err) {
    console.error("Failed to award unit completion:", err);
    alert("Could not register unit completion. Try again.");
  }
}
 else {
      alert(`You scored ${score}/${questions.length}. You need at least 8/${questions.length} to pass. Review the unit and try again.`);
    }

    // keep modal open to show results; user can close
  };

    // Fisher-Yates shuffle for arrays (non-destructive clone)
  const shuffleArray = (arr) => {
    const a = arr.map((x) => x); // shallow clone
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // Prepare and open the final big quiz (only if all units passed)
  const startFinalQuiz = () => {
    if (!completedUnits.unit1 || !completedUnits.unit2 || !completedUnits.unit3) {
      alert("You must complete all three unit tests before attempting the Final Exam.");
      return;
    }
    // create a shuffled copy of the big quiz bank
    const shuffled = shuffleArray(bigQuizBank);
    setFinalQuestions(shuffled);
    setFinalAnswers(new Array(shuffled.length).fill(null));
    setFinalIndex(0);
    setFinalScore(null);
    setFinalPassed(null);
    setFinalAttempted(true);
    setShowFinalModal(true);
  };

  // choose answer for final quiz
  const chooseFinalAnswer = (qIndex, optIndex) => {
    setFinalAnswers((prev) => {
      const copy = [...prev];
      copy[qIndex] = optIndex;
      return copy;
    });
  };

  // submit final quiz
  const submitFinalQuiz = async () => {
    if (!finalQuestions || finalQuestions.length === 0) return;
    let score = 0;
    for (let i = 0; i < finalQuestions.length; i++) {
      if (finalAnswers[i] === finalQuestions[i].correct) score++;
    }
    setFinalScore(score);
    const total = finalQuestions.length;
    // pass threshold is 85% (rounded up)
    const required = Math.ceil(total * 0.85);
    const passed = score >= required;
    setFinalPassed(passed);

    // award 3 volunteer hours on pass
// award 3 volunteer hours on pass ONLY if not already awarded
if (passed) {
  if (!user) {
    alert("You must be logged in for your completion to be recorded.");
    return;
  }

  try {
    const functions = getFunctions(undefined, "us-central1");
    const awardFinalExam = httpsCallable(functions, "awardFinalExam");

    await awardFinalExam();

    alert(`You scored ${score}/${total} — Passed!`);
  } catch (err) {
    console.error("Failed to award final exam:", err);
    alert("Could not register final exam completion. Try again.");
  }
}
 else {
      alert(`You scored ${score}/${total}. Required to pass: ${required}. Try again.`);
    }
  };

  const closeFinalModal = () => {
    setShowFinalModal(false);
    setFinalQuestions([]);
    setFinalAnswers([]);
    setFinalIndex(0);
    setFinalScore(null);
    setFinalPassed(null);
  };


  // close test modal
  const closeTestModal = () => {
    setShowTestModal(false);
    setTestUnitKey(null);
    setSelectedAnswers([]);
    setCurrentQuestionIndex(0);
    setTestScore(null);
    setTestPassed(null);
  };

  // Unit Test node click (was tryOpenUnitTest previously) — open start flow
  const tryOpenUnitTest = (unitKey) => {
    // The render and control UI will ensure the node is only clickable when available
    startUnitTest(unitKey);
  };

  // render a unit's nodes including the unit-test node at the end when appropriate
  const renderUnitNodes = (unitKey) => {
    const unit = units[unitKey];
    const nodes = [];

    for (const v of unit.videos) {
      const status = nodeStatus(unitKey, v.id);
      nodes.push(
        <div key={`${unitKey}-v-${v.id}`} className={`video-node ${status}`}>
          <div className="node-inner">
            <button
              className="node-button"
              onClick={() => tryOpenVideo(v)}
              disabled={status === "locked"}
              aria-label={`${v.title} (${status})`}
              title={v.title}
              type="button"
            >
              <img src={`./secure.png`} alt="Video" className="node-thumb" />
              <div className="number-bubble">{v.id}</div>
            </button>
            <div className="node-title">
              {v.id}. {v.title}
            </div>
          </div>
        </div>
      );
    }

    // Unit Test node
    const allWatched = (lastWatched[unitKey] || 0) >= unit.count;
    const testCompleted = !!completedUnits[unitKey];
    let testStatus = "locked";
    if (testCompleted) testStatus = "watched";
    else if (allWatched) testStatus = "available";

    nodes.push(
      <div key={`${unitKey}-test`} className={`video-node ${testStatus}`}>
        <div className="node-inner">
          <button
            className="node-button"
            onClick={() => tryOpenUnitTest(unitKey)}
            disabled={testStatus === "locked"}
            aria-label={`Unit Test (${testStatus})`}
            title={`${unit.title} — Unit Test`}
            type="button"
          >
            <img src={`./contract.png`} alt="Unit Test" className="node-thumb" />
            <div className="number-bubble">✓</div>
          </button>
          <div className="node-title">Unit Test</div>
        </div>
      </div>
    );

    return nodes;
  };

  // render tabs showing lock overlay icons for locked units
  const renderTabs = () => {
    return Object.keys(units).map((unitKey) => {
      const unlocked = unitUnlocked(unitKey);
      return (
        <div className="tab-container" key={unitKey}>
          <button
            className={`tab-btn ${activeTab === unitKey ? "active" : ""} ${!unlocked ? "locked" : ""}`}
            onClick={() => handleTabClick(unitKey)}
            disabled={!unlocked}
            type="button"
          >
            {units[unitKey].title.split("—")[0].trim()}
          </button>
          {!unlocked && <img src="./lock.png" alt="Locked" className="tab-lock" />}
        </div>
      );
    });
  };

  // badge image selector helper
  const badgeImg = () => {
    if (badge === "platinum") return ".//platinum.png";
    if (badge === "gold") return "./gold.png";
    if (badge === "silver") return "./silver.png";
    if (badge === "bronze") return "./bronze.png";
    if (badge === "none") return "No badge";
    return "";
  };

  return (
    <div className="dashboard-container">
      <header className="badge-section">
        <h1 className="dashboard-title">Echelon — Volunteer Dashboard</h1>

        <div className="badge-row">
          <div className="badge-pill">
            <img src={badgeImg()} alt={`${badge} badge`} className="badge-img" />
            <div className="badge-text">
              <strong>{badge === "none" ? "No Badge Yet" : `${badge.charAt(0).toUpperCase() + badge.slice(1)} Badge`}</strong>
              <div className="badge-sub">Earn badges through volunteering</div>
            </div>
          </div>

          <div className="progress-summary">
            Volunteer Hours: <strong>{volunteerHours}</strong>
          </div>
        </div>
      </header>

      <nav className="tabs">{renderTabs()}</nav>


      <section className="unit-card">
        <h2 className="unit-heading">{units[activeTab].title}</h2>
        <div className="tree-wrapper">
          <div className="video-tree">{renderUnitNodes(activeTab)}</div>
        </div>

<div className="controls-row">
  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
    <button
      className="quiz-button"
      onClick={() => { tryOpenUnitTest(activeTab); }}
      type="button"
    >
      Take Unit Quiz
    </button>
  </div>

  <div className="legend">
    ...
  </div>
</div>

      </section>
            <div className={`final-banner ${completedUnits.unit1 && completedUnits.unit2 && completedUnits.unit3 ? "unlocked" : "locked"}`}>
  <div className="final-banner-inner">
    <div className="final-info">
      <div className="final-title">Final Exam — Comprehensive Assessment</div>
      <div className="final-sub">
        Pass rate: <strong>85%</strong> · Reward: <strong>+3 volunteer hours</strong>
      </div>
      <div className="final-hint">
        {completedUnits.unit1 && completedUnits.unit2 && completedUnits.unit3
          ? "You have unlocked the Final Exam — good luck!"
          : "Locked — pass Unit 1, Unit 2 and Unit 3 tests to unlock."}
      </div>
    </div>

    <div className="final-action">
      <button
        className="final-cta"
        onClick={startFinalQuiz}
        disabled={!(completedUnits.unit1 && completedUnits.unit2 && completedUnits.unit3)}
        aria-disabled={!(completedUnits.unit1 && completedUnits.unit2 && completedUnits.unit3)}
        type="button"
        title={completedUnits.unit1 && completedUnits.unit2 && completedUnits.unit3 ? "Attempt Final Exam" : "Locked — complete unit tests first"}
      >
        {completedUnits.unit1 && completedUnits.unit2 && completedUnits.unit3 ? "Start Final Exam" : "Locked"}
      </button>
    </div>
  </div>
</div>

      {/* Video modal */}
{selectedVideo && (
  <div className="video-modal" onClick={closeModal}>
    <div className="video-modal-inner" onClick={(e) => e.stopPropagation()}>
  <div
  id="yt-player-container"
  style={{
    width: "100%",
    height: "500px",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
    marginBottom: "12px",
  }}
></div>
      <div className="modal-meta">
        <h3>{selectedVideo.title}</h3>
        <button className="btn-close" onClick={closeModal} type="button">
          Close
        </button>
      </div>
    </div>
  </div>
)}


      {/* Test modal */}
      {showTestModal && testUnitKey && (
        <div className="test-modal-overlay" onClick={closeTestModal}>
          <div className="test-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{units[testUnitKey].title} — Unit Test</h3>

            {/* If score already calculated, show results */}
            {testScore !== null ? (
              <div className="test-results">
                <p>
                  Your score: <strong>{testScore}/{unitQuestions[testUnitKey].length}</strong>
                </p>
                {testPassed ? (
                  <p>Passed! 1 volunteer hour credited.</p>
                ) : (
                  <p>Not passed — you need at least 8/{unitQuestions[testUnitKey].length} to pass. Review the unit and try again.</p>
                )}
                <button onClick={closeTestModal} type="button">Close</button>
              </div>
            ) : (
              // Quiz content
              <div className="quiz-content">
                <p>
                  Question {currentQuestionIndex + 1} / {unitQuestions[testUnitKey].length}
                </p>
                <div className="question-block">
                  <div className="question-text">{unitQuestions[testUnitKey][currentQuestionIndex].q}</div>
                  <div className="options-list">
                    {unitQuestions[testUnitKey][currentQuestionIndex].options.map((opt, idx) => {
                      const chosen = selectedAnswers[currentQuestionIndex] === idx;
                      return (
                        <button
                          key={idx}
                          className={`option-btn ${chosen ? "chosen" : ""}`}
                          onClick={() => chooseAnswer(currentQuestionIndex, idx)}
                          type="button"
                          aria-pressed={chosen}
                        >
                          {String.fromCharCode(65 + idx)}. {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="quiz-controls">
                  <button
                    onClick={() => setCurrentQuestionIndex((i) => Math.max(0, i - 1))}
                    disabled={currentQuestionIndex === 0}
                    type="button"
                  >
                    Previous
                  </button>
                  {currentQuestionIndex < unitQuestions[testUnitKey].length - 1 ? (
                    <button onClick={() => setCurrentQuestionIndex((i) => Math.min(unitQuestions[testUnitKey].length - 1, i + 1))} type="button">
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        // Quick validation: ensure all answers selected (optional)
                        submitTest();
                      }}
                      type="button"
                    >
                      Submit Test
                    </button>
                  )}
                </div>

                <div style={{ marginTop: 8 }}>
                  <small>Tip: You must score 8/{unitQuestions[testUnitKey].length} or higher to pass.</small>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
       {/* Final Exam modal */}
      {showFinalModal && finalQuestions && finalQuestions.length > 0 && (
        <div className="test-modal-overlay" onClick={closeFinalModal}>
          <div className="test-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Final Exam — {finalQuestions.length} Questions</h3>

            {finalScore !== null ? (
              <div className="test-results">
                <p>Your score: <strong>{finalScore}/{finalQuestions.length}</strong></p>
                <p>Required to pass: <strong>{Math.ceil(finalQuestions.length * 0.85)}</strong></p>
                {finalPassed ? <p>Passed — 3 volunteer hours credited.</p> : <p>Not passed — try again later.</p>}
                <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 12 }}>
                  <button onClick={closeFinalModal} type="button" className="quiz-button">Close</button>
                </div>
              </div>
            ) : (
              <div className="quiz-content">
                <p>Question {finalIndex + 1} / {finalQuestions.length}</p>
                <div className="question-block">
                  <div className="question-text">{finalQuestions[finalIndex].q}</div>
                  <div className="options-list">
                    {finalQuestions[finalIndex].options.map((opt, idx) => {
                      const chosen = finalAnswers[finalIndex] === idx;
                      return (
                        <button
                          key={idx}
                          className={`option-btn ${chosen ? "chosen" : ""}`}
                          onClick={() => chooseFinalAnswer(finalIndex, idx)}
                          type="button"
                          aria-pressed={chosen}
                        >
                          {String.fromCharCode(65 + idx)}. {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="quiz-controls">
                  <button onClick={() => setFinalIndex((i) => Math.max(0, i - 1))} disabled={finalIndex === 0} type="button">Previous</button>
                  {finalIndex < finalQuestions.length - 1 ? (
                    <button onClick={() => setFinalIndex((i) => Math.min(finalQuestions.length - 1, i + 1))} type="button">Next</button>
                  ) : (
                    <button onClick={submitFinalQuiz} type="button">Submit Final Exam</button>
                  )}
                </div>

                <div style={{ marginTop: 8 }}>
                  <small>Tip: pass mark is 85% of total questions ({Math.ceil((finalQuestions.length || 0) * 0.85)} required).</small>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Hours;
